import { useState, type FormEvent } from "react";

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

type TreeNodeProps = {
  label: string;
  value: unknown;
  path: string;
  selectedPath: string;
  onSelect: (_path: string) => void;
  depth: number;
};

export type ConfigPathSelectorProps = {
  config: Record<string, unknown> | null;
  selectedPath: string;
  onSelect: (_path: string) => void;
};

/**
 * A single node in the config key tree.
 *
 * Each node represents one key in the client config
 * Nodes fall into three categories object (e.g. ignitionConfig), array (e.g. policyTypes), and leaf where leaf is integer / string/ boolean / null (e.g. enhanced )
 * Selection state is controlled externally via `selectedPath` / `onSelect`.
 * The component itself owns only the expand/collapse toggle.
 */
const TreeNode = ({
  label,
  value,
  path,
  selectedPath,
  onSelect,
  depth,
}: TreeNodeProps) => {
  const isObject = isPlainObject(value);
  const isArray = Array.isArray(value);
  // Only object nodes with at least one key get a chevron and recursive children.
  const hasChildren = isObject && Object.keys(value).length > 0;

  // Auto-expand the top two levels so the tree is immediately scannable;
  // deeper nodes start collapsed to avoid visual overload.
  const [expanded, setExpanded] = useState(depth < 2);

  const isSelected = selectedPath === path;
  // Highlight any ancestor of the selected path so the user can see which
  // branch contains the current selection without it looking fully active.
  const isAncestor = Boolean(selectedPath && selectedPath.startsWith(`${path}.`));

  // Each depth level indents by 12px on top of the 8px base padding.
  const indent = depth * 12;

  const handleClick = () => {
    // Always emit this node's path — parent (object) nodes can themselves be
    // the selected path when the user wants to compare an entire sub-object.
    onSelect(path);
    if (hasChildren) setExpanded((e) => !e);
  };

  return (
    <div>
      <div
        className={`flex items-center gap-x-1 cursor-pointer rounded px-2 py-0.5 text-sm select-none group ${
          isSelected
            ? "bg-black text-white"
            : isAncestor
            ? "bg-gray-100 text-gray-800"
            : "text-gray-700 hover:bg-gray-100"
        }`}
        style={{ paddingLeft: `${8 + indent}px` }}
        onClick={handleClick}
      >
        {/* Expand/collapse chevron — only shown for non-empty object nodes */}
        {hasChildren && (
          <span className="text-xs w-3 shrink-0 text-gray-400">
            {expanded ? "▾" : "▸"}
          </span>
        )}
        {/* Spacer keeps label alignment consistent for leaf/array nodes */}
        {!hasChildren && <span className="w-3 shrink-0" />}
        <span className="font-mono truncate">{label}</span>
        {/* Array length badge — lets the user know it's an array before selecting */}
        {isArray && (
          <span className="ml-1 text-xs text-gray-400 shrink-0">
            [{value.length}]
          </span>
        )}
        {/* Scalar type hint — shows "string", "number", "boolean", or "null" */}
        {!hasChildren && !isArray && (
          <span className="ml-auto text-xs opacity-50 shrink-0">
            {value === null ? "null" : typeof value}
          </span>
        )}
      </div>

      {/* Recursively render child keys when this object node is expanded */}
      {hasChildren && expanded && (
        <div>
          {Object.entries(value).map(([key, child]) => (
            <TreeNode
              key={key}
              label={key}
              value={child}
              // Build the dot-notation path incrementally as we recurse.
              // At depth 0 the path is just the key; deeper nodes prepend
              // the parent path (e.g. "ignitionConfig" → "ignitionConfig.scopes").
              path={path ? `${path}.${key}` : key}
              selectedPath={selectedPath}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};



/**
 * Config path selector panel.
 *
 * Renders a tree of all top-level and nested config keys derived from the model client's config.
 * Emits the selected dot-notation path on change.
 *
 * Also provides a raw path input for power users who want to type a path directly.
 */
const ConfigPathSelector = ({
  config,
  selectedPath,
  onSelect,
}: ConfigPathSelectorProps) => {
  const [rawInput, setRawInput] = useState("");

  if (!config) {
    return (
      <div className="text-xs text-gray-400 px-2 py-2">
        No config available.
      </div>
    );
  }

  const handleRawSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (rawInput.trim()) {
      onSelect(rawInput.trim());
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-2 pb-2 border-b border-gray-100">
        <form onSubmit={handleRawSubmit} className="flex gap-x-1">
          <input
            type="text"
            className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-black"
            placeholder="ignitionConfig.scopes.monitoring"
            value={rawInput}
            onChange={(e) => setRawInput(e.target.value)}
          />
          <button
            type="submit"
            className="px-2 py-1 bg-gray-800 text-white text-xs rounded hover:bg-black"
          >
            Go
          </button>
        </form>
      </div>

      <div className="flex-1 overflow-y-auto py-1">
        {Object.entries(config).map(([key, value]) => (
          <TreeNode
            key={key}
            label={key}
            value={value}
            path={key}
            selectedPath={selectedPath}
            onSelect={onSelect}
            depth={0}
          />
        ))}
      </div>

      {selectedPath && (
        <div className="px-2 py-1.5 border-t border-gray-100 bg-gray-50">
          <p className="text-xs text-gray-500 font-mono truncate">
            {selectedPath}
          </p>
        </div>
      )}
    </div>
  );
};

export default ConfigPathSelector;
